import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from '../lib/toast';
import Sidebar from '../components/layout/Sidebar';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Users,
  DollarSign,
  ShoppingBag,
  Phone,
  Clock,
  Star,
  TrendingUp,
  CreditCard,
  MapPin,
  RefreshCw,
  X,
  ChevronDown,
  ChevronUp,
  Heart,
  CalendarDays,
  Timer,
  Repeat,
  AlertCircle,
  Package,
  Receipt
} from 'lucide-react';

const SalesReport = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState({ summary: {}, customers: [], period: {} });
  const [period, setPeriod] = useState('today');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [expandedOrders, setExpandedOrders] = useState({});

  useEffect(() => {
    fetchReport();
  }, [period, selectedDate]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      let url = `/cash-register/sales-crm-report?period=${period}`;
      if (period === 'custom' && customStartDate && customEndDate) {
        url += `&start_date=${customStartDate}&end_date=${customEndDate}`;
      }
      const res = await api.get(url);
      setReport(res.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Error al cargar el reporte');
    } finally {
      setLoading(false);
    }
  };

  const navigateDate = (direction) => {
    const current = new Date(selectedDate);
    let newDate;
    
    if (period === 'today') {
      newDate = new Date(current.setDate(current.getDate() + direction));
    } else if (period === 'week') {
      newDate = new Date(current.setDate(current.getDate() + (7 * direction)));
    } else if (period === 'month') {
      newDate = new Date(current.setMonth(current.getMonth() + direction));
    }
    
    setSelectedDate(newDate.toISOString().split('T')[0]);
  };

  const toggleOrderExpand = (orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const formatOrderType = (type) => {
    const types = {
      'table': 'Mesa',
      'counter': 'Mostrador',
      'delivery': 'Delivery'
    };
    return types[type] || type;
  };

  const formatPaymentMethod = (method) => {
    const methods = {
      'efectivo': 'Efectivo',
      'tarjeta': 'Tarjeta',
      'transferencia': 'Transferencia'
    };
    return methods[method] || method;
  };

  const getCustomerStatusColor = (daysSince) => {
    if (daysSince === 0) return 'bg-green-500';
    if (daysSince <= 7) return 'bg-green-400';
    if (daysSince <= 14) return 'bg-yellow-400';
    if (daysSince <= 30) return 'bg-orange-400';
    return 'bg-red-400';
  };

  const getCustomerStatusText = (daysSince) => {
    if (daysSince === 0) return 'Compró hoy';
    if (daysSince === 1) return 'Ayer';
    if (daysSince <= 7) return `Hace ${daysSince} días`;
    if (daysSince <= 14) return 'Hace 1-2 semanas';
    if (daysSince <= 30) return 'Hace 2-4 semanas';
    return `Hace ${daysSince} días - ¡Re-activar!`;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <div className="lg:ml-64 p-4 md:p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold font-chivo text-foreground">
            Reporte de Ventas
          </h1>
          <p className="text-muted-foreground mt-1">
            Análisis detallado para estrategia de marketing
          </p>
        </div>

        {/* Period Selector */}
        <Card className="mb-6 border border-border/50">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              {/* Period Buttons */}
              <div className="flex bg-slate-100 rounded-lg p-1">
                {[
                  { id: 'today', label: 'Hoy' },
                  { id: 'week', label: 'Semana' },
                  { id: 'month', label: 'Mes' },
                ].map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setPeriod(p.id)}
                    data-testid={`period-${p.id}`}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                      period === p.id
                        ? 'bg-white shadow text-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              {/* Navigation Arrows */}
              <div className="flex items-center gap-2 ml-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateDate(-1)}
                  data-testid="nav-prev"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="px-3 py-1.5 bg-slate-100 rounded-md text-sm font-medium min-w-[120px] text-center">
                  {report.period?.label || 'Cargando...'}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateDate(1)}
                  data-testid="nav-next"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchReport}
                  data-testid="refresh-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="border border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-green-100">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Ventas</p>
                  <p className="text-xl font-bold text-green-600">
                    ${report.summary?.total_amount?.toLocaleString() || '0'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-blue-100">
                  <Receipt className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Órdenes</p>
                  <p className="text-xl font-bold">{report.summary?.total_sales || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-purple-100">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Clientes</p>
                  <p className="text-xl font-bold">{report.summary?.unique_customers || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-amber-100">
                  <TrendingUp className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Ticket Promedio</p>
                  <p className="text-xl font-bold">${report.summary?.avg_ticket || '0'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content: Customer List + Detail Panel */}
        <div className="flex gap-6">
          {/* Customer List */}
          <div className={`transition-all duration-300 ${selectedCustomer ? 'w-1/2' : 'w-full'}`}>
            <Card className="border border-border/50">
              <CardHeader className="pb-3 border-b">
                <CardTitle className="font-chivo flex items-center gap-2 text-lg">
                  <Users className="w-5 h-5" />
                  Clientes del Período
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin w-8 h-8 border-4 border-accent border-t-transparent rounded-full" />
                  </div>
                ) : report.customers?.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No hay ventas en este período</p>
                  </div>
                ) : (
                  <div className="divide-y max-h-[calc(100vh-380px)] overflow-y-auto">
                    {report.customers?.map((customer, idx) => {
                      const isSelected = selectedCustomer?.customer_name === customer.customer_name &&
                                        selectedCustomer?.customer_phone === customer.customer_phone;
                      
                      return (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.03 }}
                          onClick={() => setSelectedCustomer(isSelected ? null : customer)}
                          className={`p-4 cursor-pointer transition-all hover:bg-slate-50 ${
                            isSelected ? 'bg-accent/10 border-l-4 border-l-accent' : ''
                          }`}
                          data-testid={`customer-row-${idx}`}
                        >
                          <div className="flex items-center justify-between">
                            {/* Customer Info */}
                            <div className="flex items-center gap-3">
                              <div className={`w-11 h-11 rounded-full flex items-center justify-center text-white font-bold ${
                                isSelected ? 'bg-accent' : 'bg-slate-300'
                              }`}>
                                {customer.customer_name?.charAt(0)?.toUpperCase() || 'C'}
                              </div>
                              <div>
                                <p className="font-semibold text-foreground">
                                  {customer.customer_name}
                                </p>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  {customer.customer_phone && (
                                    <span className="flex items-center gap-1">
                                      <Phone className="w-3 h-3" />
                                      {customer.customer_phone}
                                    </span>
                                  )}
                                  <span className="text-slate-300">•</span>
                                  <span>{customer.period_orders} orden{customer.period_orders !== 1 ? 'es' : ''}</span>
                                </div>
                              </div>
                            </div>

                            {/* Amount */}
                            <div className="text-right">
                              <p className="text-lg font-bold text-green-600">
                                ${customer.period_total?.toLocaleString()}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                en este período
                              </p>
                            </div>
                          </div>

                          {/* Quick Stats Bar */}
                          {!selectedCustomer && (
                            <div className="mt-3 flex items-center gap-4 text-xs">
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <Heart className="w-3 h-3 text-red-400" />
                                <span className="font-medium">{customer.lifetime_visits}</span> visitas totales
                              </div>
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <DollarSign className="w-3 h-3 text-green-500" />
                                <span className="font-medium">${customer.lifetime_total?.toLocaleString()}</span> histórico
                              </div>
                              <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-white ${getCustomerStatusColor(customer.days_since_visit)}`}>
                                <Clock className="w-3 h-3" />
                                {getCustomerStatusText(customer.days_since_visit)}
                              </div>
                            </div>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Customer Detail Panel */}
          <AnimatePresence>
            {selectedCustomer && (
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 50 }}
                className="w-1/2"
              >
                <Card className="border-0 bg-slate-800 text-white overflow-hidden sticky top-4">
                  {/* Panel Header */}
                  <div className="bg-slate-900 p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 rounded-full bg-accent flex items-center justify-center text-2xl font-bold">
                        {selectedCustomer.customer_name?.charAt(0)?.toUpperCase() || 'C'}
                      </div>
                      <div>
                        <h3 className="font-bold text-xl">{selectedCustomer.customer_name}</h3>
                        {selectedCustomer.customer_phone && (
                          <p className="text-slate-400 flex items-center gap-1">
                            <Phone className="w-3.5 h-3.5" />
                            {selectedCustomer.customer_phone}
                          </p>
                        )}
                      </div>
                    </div>
                    <button 
                      onClick={() => setSelectedCustomer(null)}
                      className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                      data-testid="close-panel"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Scrollable Content */}
                  <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
                    {/* Customer Metrics */}
                    <div className="grid grid-cols-2 gap-3 p-4 bg-slate-900/50">
                      <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-green-400">
                          ${selectedCustomer.period_total?.toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-400">Este Período</p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-accent">
                          ${selectedCustomer.lifetime_total?.toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-400">Total Histórico</p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold">{selectedCustomer.lifetime_visits}</p>
                        <p className="text-xs text-slate-400">Visitas Totales</p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold">${selectedCustomer.avg_ticket}</p>
                        <p className="text-xs text-slate-400">Ticket Promedio</p>
                      </div>
                    </div>

                    {/* Marketing Insights */}
                    <div className="p-4 space-y-4">
                      <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wide flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        Insights para Marketing
                      </h4>

                      {/* Favorite Products */}
                      {selectedCustomer.favorite_products?.length > 0 && (
                        <div className="bg-slate-700/30 rounded-lg p-3">
                          <p className="text-xs text-slate-400 mb-2 flex items-center gap-1">
                            <Star className="w-3 h-3 text-yellow-400" />
                            Productos Favoritos
                          </p>
                          <div className="space-y-1.5">
                            {selectedCustomer.favorite_products.slice(0, 5).map((prod, i) => (
                              <div key={i} className="flex items-center justify-between text-sm">
                                <span>{prod.name}</span>
                                <span className="text-slate-400">
                                  {prod.times_ordered}x · ${prod.total_spent?.toLocaleString()}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Behavioral Patterns */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-slate-700/30 rounded-lg p-3">
                          <p className="text-xs text-slate-400 mb-1 flex items-center gap-1">
                            <CalendarDays className="w-3 h-3" />
                            Día Preferido
                          </p>
                          <p className="font-semibold">{selectedCustomer.preferred_day}</p>
                        </div>
                        <div className="bg-slate-700/30 rounded-lg p-3">
                          <p className="text-xs text-slate-400 mb-1 flex items-center gap-1">
                            <Timer className="w-3 h-3" />
                            Horario Preferido
                          </p>
                          <p className="font-semibold">{selectedCustomer.preferred_hour}</p>
                        </div>
                        <div className="bg-slate-700/30 rounded-lg p-3">
                          <p className="text-xs text-slate-400 mb-1 flex items-center gap-1">
                            <CreditCard className="w-3 h-3" />
                            Método de Pago
                          </p>
                          <p className="font-semibold">{formatPaymentMethod(selectedCustomer.preferred_payment)}</p>
                        </div>
                        <div className="bg-slate-700/30 rounded-lg p-3">
                          <p className="text-xs text-slate-400 mb-1 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            Tipo de Orden
                          </p>
                          <p className="font-semibold">{formatOrderType(selectedCustomer.preferred_order_type)}</p>
                        </div>
                      </div>

                      {/* Re-engagement Alert */}
                      <div className={`rounded-lg p-3 flex items-center gap-3 ${
                        selectedCustomer.days_since_visit > 14 
                          ? 'bg-red-500/20 border border-red-500/30' 
                          : 'bg-green-500/20 border border-green-500/30'
                      }`}>
                        {selectedCustomer.days_since_visit > 14 ? (
                          <>
                            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                            <div>
                              <p className="font-semibold text-red-300">Re-activar Cliente</p>
                              <p className="text-xs text-red-300/80">
                                {selectedCustomer.days_since_visit} días sin visitarnos. 
                                {selectedCustomer.visit_frequency_days > 0 && 
                                  ` Normalmente viene cada ${selectedCustomer.visit_frequency_days} días.`
                                }
                              </p>
                            </div>
                          </>
                        ) : (
                          <>
                            <Heart className="w-5 h-5 text-green-400 flex-shrink-0" />
                            <div>
                              <p className="font-semibold text-green-300">Cliente Activo</p>
                              <p className="text-xs text-green-300/80">
                                {getCustomerStatusText(selectedCustomer.days_since_visit)}
                                {selectedCustomer.visit_frequency_days > 0 && 
                                  ` · Frecuencia: cada ${selectedCustomer.visit_frequency_days} días`
                                }
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Orders History */}
                    <div className="p-4 border-t border-slate-700">
                      <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                        <Receipt className="w-4 h-4" />
                        Órdenes del Período ({selectedCustomer.orders?.length || 0})
                      </h4>
                      <div className="space-y-2">
                        {selectedCustomer.orders?.map((order, idx) => (
                          <div 
                            key={idx} 
                            className="bg-slate-700/30 rounded-lg overflow-hidden"
                          >
                            {/* Order Header */}
                            <div 
                              className="p-3 flex items-center justify-between cursor-pointer hover:bg-slate-700/50 transition-colors"
                              onClick={() => toggleOrderExpand(order.order_id)}
                              data-testid={`order-row-${idx}`}
                            >
                              <div className="flex items-center gap-3">
                                <span className="font-mono text-accent font-bold">
                                  #{order.order_number}
                                </span>
                                <div className="text-sm">
                                  <span className="text-slate-300">{order.date}</span>
                                  <span className="text-slate-500 mx-1">·</span>
                                  <span className="text-slate-400">{order.time}</span>
                                </div>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  order.order_type === 'table' ? 'bg-blue-500/30 text-blue-300' :
                                  order.order_type === 'delivery' ? 'bg-purple-500/30 text-purple-300' :
                                  'bg-slate-500/30 text-slate-300'
                                }`}>
                                  {formatOrderType(order.order_type)}
                                  {order.table_number && ` ${order.table_number}`}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-green-400">
                                  ${order.total?.toLocaleString()}
                                </span>
                                {expandedOrders[order.order_id] ? (
                                  <ChevronUp className="w-4 h-4 text-slate-400" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-slate-400" />
                                )}
                              </div>
                            </div>

                            {/* Order Items (Expanded) */}
                            <AnimatePresence>
                              {expandedOrders[order.order_id] && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  className="border-t border-slate-600/50"
                                >
                                  <div className="p-3 space-y-1.5 bg-slate-800/50">
                                    {order.items?.map((item, i) => (
                                      <div key={i} className="flex items-center justify-between text-sm">
                                        <div className="flex items-center gap-2">
                                          <span className="text-accent font-medium">{item.quantity}x</span>
                                          <span className="text-slate-300">{item.name}</span>
                                        </div>
                                        <span className="text-slate-400">${item.subtotal?.toLocaleString()}</span>
                                      </div>
                                    ))}
                                    <div className="pt-2 mt-2 border-t border-slate-600/50 flex justify-between text-xs text-slate-400">
                                      <span>Pago: {formatPaymentMethod(order.payment_method)}</span>
                                    </div>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default SalesReport;
